/**
 * 購読（チャットの返答のストリーム）を、WebSocket と SSE のどちらで受けるかを決める。
 * 環境ごとに 1 つの値（ビルド時に埋め込む NEXT_PUBLIC_SUBSCRIPTION_TRANSPORT）で決め、空なら WebSocket。
 * SSE は新しい WebSocket 接続を作らずに済む。ブラウザごとの上書きもできる。値を変えるには、画面の再デプロイが要る。
 */

export type SubscriptionTransport = 'ws' | 'sse';

export const SUBSCRIPTION_TRANSPORT_STORAGE_KEY = 'kuden:subscription-transport';
export const SUBSCRIPTION_TRANSPORT_URL_PARAM = 'subscriptionTransport';
// 値が `default` のとき、ブラウザごとの上書きを消す
const OVERRIDE_RESET_VALUE = 'default';

type ResolveInput = {
  configured: string | undefined;
  override: string | null;
};

const isTransport = (value: string | null | undefined): value is SubscriptionTransport =>
  value === 'ws' || value === 'sse';

export function resolveSubscriptionTransport({ configured, override }: ResolveInput): SubscriptionTransport {
  if (isTransport(override)) {
    return override;
  }
  return isTransport(configured) ? configured : 'ws';
}

const readOverride = (): string | null => {
  try {
    return window.localStorage.getItem(SUBSCRIPTION_TRANSPORT_STORAGE_KEY);
  } catch {
    // ストレージが使えない環境（プライベートブラウズなど）では、上書きなしとして扱う
    return null;
  }
};

/**
 * URL の `subscriptionTransport=sse|ws|default` を、ブラウザごとの上書きとして保存する。
 * 実機（iPhone の Safari など）で、環境の設定に関係なく試すための入口。
 */
export function captureSubscriptionTransportOverride(search: string): void {
  const requested = new URLSearchParams(search).get(SUBSCRIPTION_TRANSPORT_URL_PARAM);
  try {
    if (requested === OVERRIDE_RESET_VALUE) {
      window.localStorage.removeItem(SUBSCRIPTION_TRANSPORT_STORAGE_KEY);
    } else if (isTransport(requested)) {
      window.localStorage.setItem(SUBSCRIPTION_TRANSPORT_STORAGE_KEY, requested);
    }
  } catch {
    // ストレージが使えない環境では、上書きを保存できないだけで、通信には影響しない
  }
}

export function getSubscriptionTransport(): SubscriptionTransport {
  return resolveSubscriptionTransport({
    configured: process.env.NEXT_PUBLIC_SUBSCRIPTION_TRANSPORT,
    override: readOverride(),
  });
}
